import React, { useCallback, useEffect, useMemo, useState } from 'react'
import _ from 'lodash'

import {
  ChatContextSourceEvidenceFor,
  ChatContextSourceType,
} from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import {
  ChatContext,
  ChatContextSelection,
  getEvidenceCategoryCount,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { EvidenceSVG, ControlsSVG } from 'src/components/Icons/Basic'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import {
  ContextTypeSelector,
  QuickActions,
} from '../categoryselector/CategorySelector'
import {
  ContextTypeSelectionBaseText,
  ContextTypeSelectionModalContainer,
} from '../chatContextPanel.styles'
import { ControlIdSelectorModal } from '../control/ControlModal'
import { EvidenceSelectorContainer } from './evidenceModal.styles'
import { IndividualEvidenceSelector } from './individualEvidenceSelector'

export type EvidenceModalProps = {
  show: boolean
  hide: () => void
  onBack?: () => void
  context: ChatContext
  setContext: (context: ChatContext) => void
}

const SupportedEvidenceCategories: {
  type: ChatContextSourceEvidenceFor
  label: string
  icon: React.ReactNode
}[] = [
  {
    type: ChatContextSourceEvidenceFor.FOR_CONTROL,
    label: 'Select By Control',
    icon: <ControlsSVG />,
  },
  {
    type: ChatContextSourceEvidenceFor.FOR_SELF,
    label: 'Select Specific Evidence',
    icon: <EvidenceSVG />,
  },
]

export const EvidenceModal = ({
  show,
  hide,
  onBack: originalOnBack,
  context,
  setContext,
}: EvidenceModalProps): JSX.Element => {
  const originalSelection = useMemo(
    () => context[ChatContextSourceType.EVIDENCE],
    [context],
  )
  const [newSelection, setNewSelection] =
    useState<ChatContextSelection>(originalSelection)
  const [selectedEvidenceCategory, setSelectedEvidenceCategory] = useState<
    ChatContextSourceEvidenceFor | undefined
  >(undefined)

  useEffect(() => {
    setNewSelection(originalSelection)
  }, [originalSelection])

  const handleEvidenceCategoryChange = useCallback(
    (category: ChatContextSourceEvidenceFor) => {
      setSelectedEvidenceCategory(category)
    },
    [],
  )

  const onBackToHere = useCallback(
    (clearChanges = true) => {
      setSelectedEvidenceCategory(undefined)
      if (clearChanges) {
        setNewSelection(originalSelection)
      }
    },
    [originalSelection],
  )

  const handleClose = useCallback(() => {
    onBackToHere()
    hide()
  }, [hide, onBackToHere])

  const onBackToParent = useCallback(() => {
    onBackToHere()
    originalOnBack?.()
  }, [originalOnBack, onBackToHere])

  const summary = useMemo<Record<ChatContextSourceEvidenceFor, string>>(() => {
    const { controls, individual } = getEvidenceCategoryCount(newSelection)

    return {
      [ChatContextSourceEvidenceFor.FOR_SELF]: individual
        ? `${individual} selected`
        : '',
      [ChatContextSourceEvidenceFor.FOR_CONTROL]: controls
        ? `${controls} selected`
        : '',
      [ChatContextSourceEvidenceFor.FOR_RECEPTOR]: '',
    }
  }, [newSelection])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      setContext({
        ...context,
        [ChatContextSourceType.EVIDENCE]: newSelection,
      })
    },
    [context, setContext],
  )

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onCommit(newSelection)
      if (originalOnBack) {
        onBackToParent()
      } else {
        hide()
      }
    },
    [newSelection, onCommit, originalOnBack, onBackToParent, hide],
  )

  const hasChanges = useMemo(
    () => !_.isEqual(newSelection, originalSelection),
    [newSelection, originalSelection],
  )

  const singleSelectType = useMemo<
    ChatContextSourceEvidenceFor | undefined
  >(() => {
    const evidenceCategoryTypes = Object.values(newSelection.type ?? {})
    const firstValue = evidenceCategoryTypes[0]
    const isEveryValueTheSame = evidenceCategoryTypes.every(
      (value) => value === firstValue,
    )
    return isEveryValueTheSame ? firstValue : undefined
  }, [newSelection])

  const onNewSelection = useCallback(
    (newSelection: ChatContextSelection) => {
      const included = Array.from(newSelection.included ?? [])
        // filter out other categories (for now since we want to mutually exclude the selection)
        // remove this once we want to allow multiple categories to be selected
        .filter(
          (id) =>
            newSelection.type?.[id] === undefined ||
            newSelection.type?.[id] === selectedEvidenceCategory,
        )
      const types = included.reduce((acc, currentType) => {
        acc[currentType] =
          newSelection.type?.[currentType] ??
          selectedEvidenceCategory ??
          ChatContextSourceEvidenceFor.FOR_CONTROL
        return acc
      }, {} as Record<string, number>)
      setNewSelection({
        ...newSelection,
        included: new Set(included),
        type: types,
      })
    },
    [selectedEvidenceCategory],
  )

  const resetContent = useCallback((includeAll = true) => {
    setNewSelection({
      includeAll,
      included: new Set(),
      type: {},
    })
  }, [])

  return (
    <>
      <ModalForm
        show={show && selectedEvidenceCategory === undefined}
        hide={handleClose}
        formId={ModalFormId.CHAT_CONTEXT_EVIDENCE_SELECTOR}
        size="lg"
        onBack={onBackToParent}
        hideBack={!originalOnBack}
        submitText={originalOnBack ? 'Continue' : 'Confirm'}
        disableSubmitButton={!hasChanges}
        title="Select Evidence"
      >
        <form
          id={ModalFormId.CHAT_CONTEXT_EVIDENCE_SELECTOR}
          onSubmit={onSubmit}
        >
          <ContextTypeSelectionModalContainer>
            <ContextTypeSelectionBaseText>
              You can use only one source at a time.
            </ContextTypeSelectionBaseText>
            <EvidenceSelectorContainer>
              {SupportedEvidenceCategories.map(({ type, icon, label }) => (
                <ContextTypeSelector
                  key={type}
                  icon={icon}
                  label={label}
                  summary={
                    (newSelection.includeAll && 'All Selected') ||
                    summary[type] ||
                    'Not Selected'
                  }
                  onClick={() => handleEvidenceCategoryChange(type)}
                  singleSelect={singleSelectType === type}
                  withRadius
                />
              ))}
            </EvidenceSelectorContainer>
            <QuickActions
              allIncluded={newSelection.includeAll}
              allExcluded={
                !newSelection.includeAll && !newSelection.included?.size
              }
              includeAllContent={() => resetContent()}
              excludeAllContent={() => resetContent(false)}
            />
          </ContextTypeSelectionModalContainer>
        </form>
      </ModalForm>
      <EvidenceCategoryModal
        evidenceCategory={selectedEvidenceCategory}
        close={handleClose}
        currentSelection={newSelection}
        setNewSelection={onNewSelection}
        onBack={onBackToHere}
      />
    </>
  )
}

type EvidenceCategoryModalProps = {
  evidenceCategory?: ChatContextSourceEvidenceFor
  close: () => void
  onBack: (clearChanges?: boolean) => void
  currentSelection: ChatContextSelection
  setNewSelection: (newSelection: ChatContextSelection) => void
}

const EvidenceCategoryModal = ({
  evidenceCategory,
  close,
  onBack,
  currentSelection,
  setNewSelection,
}: EvidenceCategoryModalProps): JSX.Element => {
  const back = useCallback(() => {
    onBack(false)
  }, [onBack])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection, controlIds: string[]) => {
      if (newSelection.includeAll && !currentSelection.includeAll) {
        setNewSelection({
          includeAll: false,
          included: new Set(controlIds),
          type: {},
        })
      } else {
        setNewSelection(newSelection)
      }
    },
    [setNewSelection, currentSelection],
  )

  return (
    <>
      <ControlIdSelectorModal
        show={evidenceCategory === ChatContextSourceEvidenceFor.FOR_CONTROL}
        hide={close}
        onBack={back}
        title="Select Evidence by Control"
        description="The agent will consider all evidence for selected controls. It will always consider the latest evidence at the moment."
        onCommit={onCommit}
        currentSelection={currentSelection}
      />
      <IndividualEvidenceSelector
        currentSelection={currentSelection}
        onCommit={setNewSelection}
        show={evidenceCategory === ChatContextSourceEvidenceFor.FOR_SELF}
        hide={close}
        onBack={back}
      />
    </>
  )
}
